import * as React from 'react';
import Loading from 'src/Loading';
import {PickerRow} from 'src/PickerRow';
import PopoverBox from 'src/SearchPopover/PopoverBox';
import SelectionBox from 'src/SearchPopover/SelectionBox';
import type {FocusControls} from 'src/FocusTrap';

export type RenderSelectionProps = {
  query: string;
  onChange: (value: string) => void;
  onKeyDown: (e: React.KeyboardEvent) => void;
};

export type RenderCreateRowProps = {
  item: string;
  selected: boolean;
  onSelect: () => void;
};

export type RenderOptionRowProps<Option> = {
  option: Option;
  selected: boolean;
  onSelect: () => void;
  key: number;
};

export const renderOptionDefault =
  <Option extends unknown>(render: (option: Option) => React.ReactNode) =>
  (props: RenderOptionRowProps<Option>) => {
    const {option, key, ...rowProps} = props;
    return (
      <PickerRow key={key} {...rowProps}>
        {render(option)}
      </PickerRow>
    );
  };

export const renderOptionEmptyIcon =
  <Option extends unknown>(render: (option: Option) => React.ReactNode) =>
  (props: RenderOptionRowProps<Option>) => {
    const {option, key, ...rowProps} = props;
    return (
      <PickerRow showIcon key={key} {...rowProps}>
        {render(option)}
      </PickerRow>
    );
  };

export type Props<Option> = {
  creatable?: {
    onCreate: (value: string) => void;
    shouldRenderCreate: (query: string, options: Option[]) => boolean;
    renderCreate: (props: RenderCreateRowProps) => React.ReactNode;
  };
  initialQuery?: string;
  focusControlsRef?: React.MutableRefObject<FocusControls | null>;
  onCancel?: () => void;
  onClear?: () => void;
  onSearch: (query: string) => Option[] | false;
  onSubmit?: (value: Option) => void;
  renderOption: (props: RenderOptionRowProps<Option>) => React.ReactNode;
  renderSelection: (props: RenderSelectionProps) => React.ReactNode;
  showSearchIcon?: boolean;
};

const SearchBox = <Option extends unknown>(props: Props<Option>) => {
  const {
    creatable,
    initialQuery,
    onCancel,
    onClear,
    onSearch,
    onSubmit,
    renderOption,
    renderSelection,
    showSearchIcon,
  } = props;
  const [query, setQuery] = React.useState<string>(initialQuery || '');
  const [selection, setSelection] = React.useState<number | null>(null);
  const searchResults = onSearch(query);
  const totalRows = searchResults
    ? searchResults.length + (creatable ? 1 : 0)
    : 0;

  const transformIndex = (i: number) => {
    if (i < 0 || i > totalRows - 1) {
      return null;
    } else {
      return i;
    }
  };

  const handleSubmitIndex = (i: number, results: Option[]) => {
    if (i >= 0 && i < results.length) {
      onSubmit && onSubmit(results[i]);
    } else if (creatable && i === results.length) {
      creatable && creatable.onCreate(query);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (searchResults) {
      if (e.key === 'ArrowUp') {
        if (selection === null) {
          setSelection(totalRows - 1);
        } else {
          setSelection(transformIndex(selection - 1));
        }
      } else if (e.key === 'ArrowDown') {
        if (selection === null) {
          setSelection(0);
        } else {
          setSelection(transformIndex(selection + 1));
        }
      } else if (e.key === 'Enter') {
        if (selection === null) {
          if (creatable) {
            handleSubmitIndex(searchResults.length, searchResults);
            props.focusControlsRef?.current?.focusForward();
          } else {
            onCancel && onCancel();
          }
        } else {
          handleSubmitIndex(selection, searchResults);
          props.focusControlsRef?.current?.focusForward();
        }
        e.preventDefault();
      } else if (e.key === 'Tab') {
        if (selection === null) {
          if (query === '') {
            onClear && onClear();
          } else {
            handleSubmitIndex(0, searchResults);
          }
        } else {
          handleSubmitIndex(selection, searchResults);
        }
      }
    }
  };

  const handleSubmit = (option: Option) => {
    onSubmit && onSubmit(option);
  };

  const handleCreate = (query: string) => {
    creatable && creatable.onCreate(query);
    setQuery('');
  };

  const handleInputChange = (value: string) => {
    setQuery(value);
    setSelection(null);
  };

  const createRow =
    creatable &&
    searchResults &&
    creatable.shouldRenderCreate(query, searchResults)
      ? creatable.renderCreate({
          item: query,
          selected: selection === totalRows - 1,
          onSelect: () => handleCreate(query),
        })
      : null;

  const results = searchResults ? (
    <>
      {searchResults.map((option: Option, index: number) =>
        renderOption({
          option,
          selected: selection === index,
          onSelect: () => handleSubmit(option),
          key: index,
        }),
      )}
      {createRow}
    </>
  ) : (
    <Loading size="medium" />
  );

  return (
    <PopoverBox transform={showSearchIcon ? {$x: -22} : {}}>
      <SelectionBox>
        {renderSelection({
          query,
          onChange: handleInputChange,
          onKeyDown: handleKeyDown,
        })}
      </SelectionBox>
      <div className="flex max-w-[400px] flex-col justify-center">{results}</div>
    </PopoverBox>
  );
};

export default SearchBox;
